import {
  ChevronUp,
  LoaderCircle,
  Paperclip,
  Sparkles,
  TriangleAlert,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  AiBookingDraft,
  BookingAttachmentMode,
  BookingBooker,
  BookingClarification,
  BookingDuplicateResult,
  BookingPrefill,
  BookerSuggestions,
  DraftBookingResult,
} from '../../../api/comms';
import { type DispatchClient, resolveFirstMatch, type VehicleTypeItem } from '../../../api/dispatch';
import { ApiError } from '../../../api/envelope';
import type { Conversation, ConversationBooking } from '../../../api/types';
import { useTimeFormat } from '../../../lib/tenantTime';
import { instantFromWall } from '../../../lib/timeZone';
import { useDismissOnOutside } from '../../../lib/useDismiss';
import { buildDraftJobData, draftExpiresAt } from './draftPayload';
import { buildJobPayload } from './payload';
import {
  applyBookerPick,
  bookerVerified,
  type BookingFormState,
  type BookerPick,
  type BookerType,
  type BookingSectionId,
  countsFromVehicle,
  fuzzyVehicleMatch,
  type LegState,
  type MissingKey,
  missingInSection,
  type PaymentMode,
  pickupAirportCode,
  PREFILL_ON,
  seedFromDraft,
  validateForSave,
  withVehicleCapacities,
} from './bookingForm';
import { AddressField } from './AddressField';
import { FormSection } from './FormSection';
import { type CardLookup, PaymentSection } from './PaymentSection';
import { resolveQuestions, type UiQuestion } from './questions';
import { JobLink } from './JobLink';
import { BookerPicker } from './BookerPicker';
import type { BookerLinks } from './bookerLinks';
import { topCandidates } from './bookerCandidates';
import { InfoTip } from '../InfoTip';
import { clientRef, ourRef } from '../../../lib/reservationRef';

/**
 * The AI booking box: generate → (clarify ⇄ generate) → editable form →
 * draft/save into dispatch-be. The parent remounts it (via key) per request,
 * so every state here dies with the conversation or a new Book-now click.
 */

type Phase = 'generating' | 'needs_info' | 'attachments' | 'editing' | 'error';

/** What the size prompt is about — the files, and how much of them there is. */
type AttachmentPrompt = { files: { name: string; size: number }[]; totalBytes: number };

/**
 * Local rather than shared: the only other copy lives in the website widget,
 * which is its own bundle on a tenant's critical path and does not import from
 * the console.
 */
const fmtSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
/**
 * A draft has no job/quote of its own — dispatch stores one parked booking and
 * the choice is made by the button pressed when it is converted.
 */
type SubmitKind = 'draft' | 'saveJob' | 'saveQuote';

const ALL_CLOSED: Record<BookingSectionId, boolean> = {
  trip: false,
  passenger: false,
  booker: false,
  vehicle: false,
  notes: false,
  payment: false,
};

/**
 * Open a date/time input's native picker from a click anywhere in the field.
 * Browsers only open it from the small indicator icon; agents click the middle
 * of the field and get a text caret instead of a calendar or clock. Guarded
 * because showPicker throws in a cross-origin iframe — the /embed dashboard
 * case — and is missing on older browsers; typing still works both places.
 */
const openNativePicker = (e: React.MouseEvent<HTMLInputElement>) => {
  try {
    e.currentTarget.showPicker?.();
  } catch {
    /* embed or unsupported — the field still accepts typed input */
  }
};

export interface BookingSaved {
  /** A job/quote id, or — for `kind: 'draft'` — a DRAFT id from another table. */
  dispatchId: string;
  /** Always false on a draft: dispatch does not park the distinction. */
  isQuote: boolean;
  kind: 'draft' | 'job';
  summary: string;
  /**
   * What the thread's booking strip renders, captured HERE because this is the
   * only place that has it: dispatch parks a draft where the CRM cannot read it
   * back, and a job that is edited over there afterwards should still say in the
   * strip what this agent actually sent.
   */
  bookerReference: string | null;
  /** The message the draft was read from, when the run was scoped to one. */
  messageId: number | null;
  /** UTC instant. The form holds a WALL time in the pickup's own zone. */
  pickupAt: string | null;
  trip: ConversationBooking['trip'];
}

/**
 * The card's snapshot, built from the form at the moment it was submitted.
 * `timeZone` rides along so the strip can render the pickup at the curb's own
 * clock rather than the office's — the rule the contact panel already follows.
 */
const tripSnapshot = (form: BookingFormState, vehicleName: string | null): ConversationBooking['trip'] => {
  const pax = Number.parseInt(form.passengerCount, 10);
  const lug = Number.parseInt(form.luggageCount, 10);
  const passenger = [form.passengerFirstName, form.passengerLastName]
    .map((part) => part.trim())
    .filter(Boolean)
    .join(' ');
  return {
    ...(form.pickup.text.trim() ? { pickup: form.pickup.text.trim() } : {}),
    ...(form.dropoff.text.trim() ? { dropoff: form.dropoff.text.trim() } : {}),
    ...(form.stops.length
      ? { stops: form.stops.map((stop) => stop.text.trim()).filter(Boolean) }
      : {}),
    ...(passenger ? { passenger } : {}),
    ...(vehicleName ? { vehicle: vehicleName } : {}),
    serviceType: form.serviceType,
    ...(form.timeZoneId ? { timeZone: form.timeZoneId } : {}),
    ...(Number.isInteger(pax) && pax >= 0 ? { passengerCount: pax } : {}),
    ...(Number.isInteger(lug) && lug >= 0 ? { luggageCount: lug } : {}),
  };
};

const summaryLine = (
  form: BookingFormState,
  zoneSuffix: string,
  fmtWallTime: (wall: string) => string,
): string => {
  if (form.summary) return form.summary;
  const route = [form.pickup.text, form.dropoff.text].filter(Boolean).join(' → ');
  // The pickup time is a WALL time in the pickup's own zone, so it is rendered
  // literally and tagged — converting it into the dispatch zone would move the
  // trip by hours, and an untagged out-of-region pickup reads as local. The
  // tenant's own pattern still decides its shape, hence the bound formatter.
  const when = form.pickupDatetime ? `${fmtWallTime(form.pickupDatetime)}${zoneSuffix}` : '';
  return [form.serviceType === 'hourly' ? 'hourly' : 'one-way', route, when]
    .filter(Boolean)
    .join(' · ');
};

export function AiBookingBox({
  conversation,
  fromMessageId,
  draftBooking,
  checkDuplicates,
  bookerLinks,
  dispatch,
  onForbidden,
  onClose,
  onSaved,
}: {
  conversation: Conversation;
  /**
   * Set when opened from a message's "Draft booking" action, and the extraction
   * then reads THAT MESSAGE ALONE — not the thread up to it. Unset is the
   * header's "Book now", which reads the whole conversation.
   */
  fromMessageId?: number;
  draftBooking: (body: {
    conversationDisplayId: number;
    messageId?: number;
    clarifications?: BookingClarification[];
  }) => Promise<DraftBookingResult>;
  /**
   * Re-run the duplicate check against a reference the AGENT typed. The
   * draft-time answer covers only what the model read out of the thread, and
   * the field it read it into is editable — which is precisely the case where
   * a dispatcher is correcting a number and most wants to be told.
   */
  checkDuplicates: (
    displayId: number,
    refs: { ourReference?: string | null; bookerReference?: string | null },
  ) => Promise<BookingDuplicateResult>;
  /**
   * Search dispatch for the booker, and remember a pick on the contact. Absent,
   * the chip offers only what the draft found — the box still books.
   */
  bookerLinks?: BookerLinks;
  dispatch: DispatchClient;
  onForbidden: () => void;
  onClose: () => void;
  onSaved: (saved: BookingSaved) => void;
}) {
  const { foreignZoneAbbr, fmtWallTime } = useTimeFormat();
  const [phase, setPhase] = useState<Phase>('generating');
  const [error, setError] = useState<string | null>(null);
  const [questions, setQuestions] = useState<UiQuestion[]>([]);
  const [answers, setAnswers] = useState<string[]>([]);
  /** Per choice-question: the agent opened the free-text "other" input. */
  const [customOpen, setCustomOpen] = useState<boolean[]>([]);
  const [partial, setPartial] = useState<AiBookingDraft | null>(null);
  // Kept beside `partial` rather than folded into it: "Use what it found"
  // must seed the same booker the ready branch would have, and an older
  // service that sends no booker at all leaves this null — a guest booking.
  const [booker, setBooker] = useState<BookingBooker | null>(null);
  // Only sent when the draft found nobody exact: the likely accounts (same
  // name, same company domain, colleagues already linked). Never applied on
  // their own — they are offered, and an agent picks.
  const [bookerSuggestions, setBookerSuggestions] = useState<BookerSuggestions | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  // On by default: the whole point is that the next mail from this address
  // opens on the right account. Off is the assistant booking for someone else.
  const [remember, setRemember] = useState(true);
  const [remembering, setRemembering] = useState(false);
  // A failure keeps its whole sentence on screen; a success is a short line
  // whose explanation sits behind a "?".
  const [rememberNote, setRememberNote] = useState<{ ok: boolean; text: string; tip?: string } | null>(
    null,
  );
  // The tenant's two form-filling switches, for the same reason the booker is
  // kept here: "Use what it found" seeds off the needs_info branch and must
  // obey the same ones. An older service sends none, which means fill — what
  // this box has always done.
  const [prefill, setPrefill] = useState<BookingPrefill>(PREFILL_ON);
  const [oversize, setOversize] = useState<AttachmentPrompt | null>(null);
  const [form, setForm] = useState<BookingFormState | null>(null);
  const [vehicles, setVehicles] = useState<VehicleTypeItem[] | null>(null);
  const [vehiclesError, setVehiclesError] = useState<string | null>(null);
  const [cardFetch, setCardFetch] = useState<{ customerId: number; result: CardLookup } | null>(
    null,
  );
  const [submitting, setSubmitting] = useState<SubmitKind | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [menu, setMenu] = useState<'save' | null>(null);
  /**
   * Bookings already on file carrying one of this request's references.
   *
   * Null means nothing has been asked — the box is still generating, or the
   * service predates the check. `{checked: false}` means it was asked and could
   * not answer, which is a DIFFERENT thing from finding nothing and is worded
   * differently: a silent all-clear is the failure the check exists to prevent.
   */
  const [duplicates, setDuplicates] = useState<BookingDuplicateResult | null>(null);
  /**
   * OUR reference as the model read it out of the thread. Not a form field:
   * nothing books it, and an agent has no business editing a number that came
   * off our own confirmation. Kept so the re-check below can ask about both.
   */
  const ourReference = useRef<string | null>(null);
  const clarificationsRef = useRef<BookingClarification[]>([]);
  /**
   * The answer to the size prompt. It has to ride on EVERY later round too — a
   * clarification answer re-runs the extraction, and re-asking about the same
   * pile of files each time would be unusable — so every caller of `generate`
   * passes it, and the deciding one passes its own new value rather than
   * waiting a render for the state to catch up.
   */
  const [attachmentMode, setAttachmentMode] = useState<BookingAttachmentMode>('auto');
  const mounted = useRef(true);
  useEffect(() => {
    // Re-arm on the effect body too: StrictMode's simulated unmount would
    // otherwise leave this false forever and every async result gets dropped.
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const patchLeg = useCallback((key: 'pickup' | 'dropoff' | number, leg: LegState) => {
    setForm((prev) => {
      if (!prev) return prev;
      if (key === 'pickup') return { ...prev, pickup: leg };
      if (key === 'dropoff') return { ...prev, dropoff: leg };
      const stops = [...prev.stops];
      stops[key] = leg;
      return { ...prev, stops };
    });
  }, []);

  /**
   * Like patchLeg, but only while the field still shows `expectText` — a slow
   * silent resolve must never clobber an address the agent started correcting,
   * and stale coordinates behind a green checkmark would book the wrong pickup.
   */
  const patchLegIfTextIs = useCallback(
    (key: 'pickup' | 'dropoff' | number, expectText: string, leg: LegState) => {
      setForm((prev) => {
        if (!prev) return prev;
        const current =
          key === 'pickup' ? prev.pickup : key === 'dropoff' ? prev.dropoff : prev.stops[key];
        if (!current || current.text !== expectText) return prev;
        if (key === 'pickup') return { ...prev, pickup: leg };
        if (key === 'dropoff') return { ...prev, dropoff: leg };
        const stops = [...prev.stops];
        stops[key] = leg;
        return { ...prev, stops };
      });
    },
    [],
  );

  /** Silent first-match resolve of one AI-extracted leg, then the tz lookup. */
  const autoResolve = useCallback(
    async (key: 'pickup' | 'dropoff' | number, leg: LegState, applyOn?: 'O' | 'H') => {
      const text = leg.text;
      if (!text.trim()) return;
      patchLegIfTextIs(key, text, { ...leg, resolving: true });
      try {
        const resolved = await resolveFirstMatch(dispatch, text, applyOn);
        if (!mounted.current) return;
        if (!resolved) {
          patchLegIfTextIs(key, text, { ...leg, resolving: false, error: null });
          return;
        }
        patchLegIfTextIs(key, text, {
          text,
          resolving: false,
          resolved: resolved.detail,
          matchedText: resolved.matchedText,
          error: null,
        });
        if (key === 'pickup') {
          const zone = await dispatch
            .getTimezone(
              resolved.detail.latitude,
              resolved.detail.longitude,
              new Date().toISOString(),
            )
            .catch(() => null);
          // Null zone is fine — payload falls back to the browser zone and
          // omits data.timeZone, same as the dashboard when its lookup fails.
          // Only apply while OUR resolution is still the one on the field.
          if (mounted.current && zone) {
            setForm((prev) =>
              prev && prev.pickup.resolved?.placeId === resolved.detail.placeId
                ? { ...prev, timeZoneId: zone }
                : prev,
            );
          }
        }
      } catch (e) {
        if (!mounted.current) return;
        patchLegIfTextIs(key, text, {
          ...leg,
          resolving: false,
          error: e instanceof ApiError ? e.message : 'Could not look up that address.',
        });
      }
    },
    [dispatch, patchLegIfTextIs],
  );

  const seedForm = useCallback(
    (draft: AiBookingDraft, resolved: BookingBooker | null, switches: BookingPrefill) => {
      const seeded = seedFromDraft(draft, conversation.contact, resolved, switches);
      setForm(seeded);
      setPhase('editing');
      // Vehicle catalogue: doubles as the "can the browser reach dispatch"
      // canary — a failure shows a banner and Save stays gated on vehicle.
      dispatch
        .listVehicleTypes()
        .then((list) => {
          if (!mounted.current) return;
          // Dispatch's list is ids and names; the capacities come from the
          // service, which reads them out of dispatch's own table.
          const withCapacity = withVehicleCapacities(list, switches.vehicleCapacities);
          setVehicles(withCapacity);
          const match = fuzzyVehicleMatch(draft.vehiclePreference, withCapacity);
          if (!match) return;
          // The AI's vehicle wish picks the vehicle, so it also supplies the
          // counts — the same thing choosing one by hand does below.
          setForm((prev) =>
            prev
              ? {
                  ...prev,
                  vehicleTypeId: match,
                  ...countsFromVehicle(
                    prev,
                    withCapacity.find((v) => v.id === match),
                    switches,
                  ),
                }
              : prev,
          );
        })
        .catch((e) => {
          if (!mounted.current) return;
          setVehicles([]);
          setVehiclesError(
            e instanceof ApiError ? e.message : 'Could not reach dispatch from the browser.',
          );
        });
      const applyOn = seeded.serviceType === 'hourly' ? 'H' : 'O';
      void autoResolve('pickup', seeded.pickup, applyOn);
      if (!seeded.dropoffSameAsPickup) void autoResolve('dropoff', seeded.dropoff, applyOn);
      seeded.stops.forEach((stop, index) => void autoResolve(index, stop));
    },
    [conversation.contact, dispatch, autoResolve],
  );

  const generate = useCallback(
    async (clarifications: BookingClarification[], mode: BookingAttachmentMode) => {
      setPhase('generating');
      setError(null);
      try {
        const result = await draftBooking({
          conversationDisplayId: conversation.id,
          ...(fromMessageId ? { messageId: fromMessageId } : {}),
          ...(clarifications.length ? { clarifications } : {}),
          ...(mode !== 'auto' ? { attachments: mode } : {}),
        });
        if (!mounted.current) return;
        if (result.status === 'ready') {
          setBooker(result.booker ?? null);
          setBookerSuggestions(result.bookerSuggestions ?? null);
          const switches = result.prefill ?? PREFILL_ON;
          setPrefill(switches);
          ourReference.current = result.draft.ourReference ?? null;
          setDuplicates(result.duplicates ?? null);
          seedForm(result.draft, result.booker ?? null, switches);
        } else if (result.status === 'attachments_too_large') {
          // Nothing was generated and nothing was billed — the run stopped to
          // ask. Whichever button is pressed re-runs it with that answer.
          setOversize(result.attachments);
          setPhase('attachments');
        } else {
          const items = resolveQuestions(result.questions, result.questionItems);
          setQuestions(items);
          setAnswers(items.map(() => ''));
          setCustomOpen(items.map(() => false));
          setPartial(result.partial ?? null);
          setBooker(result.booker ?? null);
          setBookerSuggestions(result.bookerSuggestions ?? null);
          setPrefill(result.prefill ?? PREFILL_ON);
          // On this branch too: a thread that quoted a reservation number and
          // left the pickup time to be asked about is exactly where the answer
          // is already known, and a dispatcher made to answer three questions
          // before being told the trip is on file has done the work twice.
          ourReference.current = result.partial?.ourReference ?? null;
          setDuplicates(result.duplicates ?? null);
          setPhase('needs_info');
        }
      } catch (e) {
        if (!mounted.current) return;
        if (e instanceof ApiError && e.kind === 'forbidden') onForbidden();
        setError(e instanceof ApiError ? e.message : 'The draft could not be generated.');
        setPhase('error');
      }
    },
    [conversation.id, fromMessageId, draftBooking, seedForm, onForbidden],
  );

  // One generation per mount — the parent's key remounts for each new request.
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void generate([], 'auto');
  }, [generate]);

  // Whose cards to show — null while the form is still generating and for a
  // guest booking alike, which is the same answer either way: there are none.
  // An AFFILIATE booker is also none: dispatch keeps affiliate cards behind
  // /stripe/affiliate/payment-methods, a different route under a different
  // permission, and offering a card we cannot charge is worse than offering
  // the payment link and bill-later a farm-in uses anyway.
  const payingCustomerId =
    form && form.bookerType === 'customer' ? (form.bookerCustomer?.id ?? null) : null;
  // Derived, not stored: only the fetch result is state, so switching customers
  // reads as `loading` on the very render that switched rather than one later.
  const cards: CardLookup = !payingCustomerId
    ? { status: 'guest' }
    : cardFetch?.customerId === payingCustomerId
      ? cardFetch.result
      : { status: 'loading' };

  useEffect(() => {
    if (!payingCustomerId) return;
    let live = true;
    dispatch
      .listCustomerCards(payingCustomerId)
      .then((list) => {
        if (!live || !mounted.current) return;
        setCardFetch({ customerId: payingCustomerId, result: { status: 'ready', cards: list } });
        // Preselect the first card, never over a choice already made. Safe
        // because the radios do not exist until this resolves, so a form still
        // holding the seeded bill-later is a form nobody has touched.
        if (list.length) {
          setForm((prev) =>
            prev &&
            prev.bookerType === 'customer' &&
            prev.bookerCustomer?.id === payingCustomerId &&
            prev.paymentMode === 'billLater' &&
            !prev.paymentMethodId
              ? { ...prev, paymentMode: 'card', paymentMethodId: list[0].id }
              : prev,
          );
        }
      })
      .catch((e) => {
        if (!live || !mounted.current) return;
        // Never fatal: dispatch guards this on `customer_payment_cards`, which
        // plenty of agents who may create a job do not hold. They can still
        // send a payment link or bill later.
        setCardFetch({
          customerId: payingCustomerId,
          result: {
            status: 'error',
            message: e instanceof ApiError ? e.message : 'dispatch did not answer.',
          },
        });
      });
    return () => {
      live = false;
    };
  }, [payingCustomerId, dispatch]);

  const setPayment = useCallback((mode: PaymentMode, methodId: string) => {
    setForm((prev) => (prev ? { ...prev, paymentMode: mode, paymentMethodId: methodId } : prev));
  }, []);

  /**
   * Attaching or detaching the dispatch customer also drops a selected card —
   * it belongs to that customer, and dispatch cannot charge it on a booking
   * that no longer names them.
   */
  const setBookerType = useCallback((type: BookerType) => {
    setForm((prev) => {
      if (!prev) return prev;
      const next = { ...prev, bookerType: type };
      // A selected card belongs to the customer that was named; dispatch cannot
      // charge it on a job that now names an affiliate, or nobody.
      if (prev.paymentMode !== 'card' || type === prev.bookerType) return next;
      return { ...next, paymentMode: 'billLater', paymentMethodId: '' };
    });
  }, []);

  const contact = conversation.contact;
  const rememberFor = contact.email ?? contact.phoneNumber ?? null;
  /**
   * The agent chose a booker the draft did not find. The form takes it at
   * once — the booking is what they are doing — and the contact learns it
   * behind that, so a failure to remember costs next time, never this booking.
   */
  const pickBooker = useCallback(
    async (pick: BookerPick) => {
      const keep = !!bookerLinks && remember && !!rememberFor;
      setForm((prev) => (prev ? applyBookerPick(prev, pick, { remembered: keep }) : prev));
      setPickerOpen(false);
      setRememberNote(null);
      if (!keep || !bookerLinks) return;
      setRemembering(true);
      try {
        if (pick.type === 'customer') {
          // One address books one way: a customer picked here forgets a desk
          // this address was remembered as.
          await bookerLinks.linkCustomer(contact.id, pick.customer.id, { clearAffiliate: true });
        } else {
          await bookerLinks.linkAffiliate(contact.id, pick.affiliate.id);
        }
        if (!mounted.current) return;
        setRememberNote({
          ok: true,
          text: 'Saved for next time',
          tip: `The next reservation drafted from ${rememberFor} opens on this booker.`,
        });
      } catch (e) {
        if (!mounted.current) return;
        setRememberNote({
          ok: false,
          text: `Booking with it, but it could not be saved for next time: ${
            e instanceof ApiError ? e.message : 'the request failed.'
          }`,
        });
      } finally {
        if (mounted.current) setRemembering(false);
      }
    },
    [bookerLinks, remember, rememberFor, contact.id],
  );

  const decideAttachments = (mode: BookingAttachmentMode) => {
    setAttachmentMode(mode);
    void generate(clarificationsRef.current, mode);
  };

  const answerAndRegenerate = () => {
    const fresh = questions
      .map((question, index) => ({ question: question.text, answer: answers[index]?.trim() ?? '' }))
      .filter((c) => c.answer);
    // Rounds compound: earlier answers keep steering later generations.
    clarificationsRef.current = [...clarificationsRef.current, ...fresh];
    void generate(clarificationsRef.current, attachmentMode);
  };

  // Tag the wall time with the pickup's zone in the note we post back, but only
  // when that zone is not the dispatch clock — see EditingBody.
  const pickupAbbr = foreignZoneAbbr(form?.timeZoneId ?? null);
  const zoneSuffix = pickupAbbr ? ` ${pickupAbbr}` : '';

  const submit = async (kind: 'draft' | 'job', isQuote: boolean) => {
    if (!form) return;
    setMenu(null);
    setSubmitError(null);
    setSubmitting(kind === 'draft' ? 'draft' : isQuote ? 'saveQuote' : 'saveJob');
    try {
      const result =
        kind === 'draft'
          ? await dispatch.createDraft({
              // A wholly different payload from a job's: the drafts table holds
              // the dashboard Add Job form's own blob — see draftPayload.ts.
              jobData: buildDraftJobData(form, {
                conversationId: conversation.id,
                contact: conversation.contact,
                vehicle: vehicles?.find((v) => v.id === form.vehicleTypeId) ?? null,
              }),
              expiredAt: draftExpiresAt(),
            })
          : await dispatch.createJob(buildJobPayload(form, { isQuote }));
      const vehicle = vehicles?.find((v) => v.id === form.vehicleTypeId) ?? null;
      // A wall time in the PICKUP's zone becomes the instant the wire carries.
      // Without the zone it is not an instant at all, so it is left out rather
      // than resolved against whichever laptop this is — the strip then shows
      // the trip line and no clock, which is honest.
      const pickup = form.pickupDatetime
        ? instantFromWall(form.pickupDatetime, form.timeZoneId || null)
        : null;
      onSaved({
        dispatchId: result.id,
        isQuote: kind === 'job' && isQuote,
        kind,
        summary: summaryLine(form, zoneSuffix, fmtWallTime),
        bookerReference: form.bookerReference.trim() || null,
        messageId: fromMessageId ?? null,
        pickupAt: form.timeZoneId && pickup ? pickup.toISOString() : null,
        trip: tripSnapshot(form, vehicle?.name ?? null),
      });
    } catch (e) {
      if (!mounted.current) return;
      // Deliberately NOT onForbidden here: a dispatch-be 1036 means "no
      // job-create permission over there", not "no crm_send here" — flipping
      // sendDenied would read-only the whole console and unmount this form.
      // Dispatch 1001 messages are user-facing; show them verbatim. The
      // booking may NOT be retried blindly — creation is not idempotent.
      setSubmitError(e instanceof ApiError ? e.message : 'Dispatch did not accept the reservation.');
      setSubmitting(null);
    }
  };

  /**
   * Re-ask whenever the reference the agent can edit changes.
   *
   * Debounced, because this fires on every keystroke in a field somebody is
   * typing a reservation number into and each run is a dispatch query. The
   * first value is skipped by comparing against what the draft already
   * answered for: re-asking the same question the moment the form opens would
   * spend a query to get back the answer that is already on screen.
   */
  const bookerRef = form?.bookerReference.trim() ?? '';
  const askedFor = useRef<string | null>(null);
  useEffect(() => {
    if (phase !== 'editing') return;
    const ours = ourReference.current;
    const key = `${ours ?? ''}|${bookerRef}`;
    if (askedFor.current === null) {
      // What the draft response already answered. Recorded, never re-asked.
      askedFor.current = key;
      return;
    }
    if (askedFor.current === key) return;
    const timer = setTimeout(() => {
      askedFor.current = key;
      if (!ours && !bookerRef) {
        // Nothing left to ask about — an agent who cleared the field is telling
        // us this is not that booking, and a stale warning would outlive it.
        setDuplicates(null);
        return;
      }
      checkDuplicates(conversation.id, { ourReference: ours, bookerReference: bookerRef })
        .then((result) => {
          if (mounted.current) setDuplicates(result);
        })
        .catch(() => {
          // The form still saves. A check that could not run is not a reason to
          // block a booking the dispatcher has decided to make.
          if (mounted.current) setDuplicates(null);
        });
    }, 450);
    return () => clearTimeout(timer);
  }, [phase, bookerRef, checkDuplicates, conversation.id]);

  const scopeHint = fromMessageId ? ' · this message only' : '';
  const title = form?.summary || 'AI draft reservation';

  return (
    <div className="aib">
      <div className="aib-head">
        <Sparkles size={14} />
        <span className="aib-title">
          {title}
          <span className="aib-scope">{scopeHint}</span>
        </span>
        <button type="button" className="aib-close" title="Discard" onClick={onClose}>
          <X size={14} />
        </button>
      </div>

      {phase === 'generating' && (
        <div className="aib-busy">
          <LoaderCircle size={14} className="spin-anim" />{' '}
          {attachmentMode === 'include'
            ? 'Reading the attachments…'
            : fromMessageId
              ? 'Reading the selected message…'
              : 'Reading the conversation…'}
        </div>
      )}

      {phase === 'attachments' && oversize && (
        <div className="aib-warn">
          <p className="aib-qa-lead">
            <Paperclip size={13} /> {oversize.files.length === 1 ? 'This' : 'These'}{' '}
            {oversize.files.length === 1 ? 'attachment is' : `${oversize.files.length} attachments are`}{' '}
            large ({fmtSize(oversize.totalBytes)}). Reading{' '}
            {oversize.files.length === 1 ? 'it' : 'them'} takes longer and costs more.
          </p>
          <ul className="aib-files">
            {oversize.files.map((file) => (
              <li key={file.name}>
                <span className="aib-file-name">{file.name}</span>
                <span className="aib-file-size">{fmtSize(file.size)}</span>
              </li>
            ))}
          </ul>
          <div className="aib-actions">
            <button
              type="button"
              className="cbtn cbtn-sm"
              onClick={() => decideAttachments('skip')}
            >
              Skip {oversize.files.length === 1 ? 'it' : 'them'}
            </button>
            <button
              type="button"
              className="cbtn cbtn-sm cbtn-primary"
              onClick={() => decideAttachments('include')}
            >
              Read anyway
            </button>
          </div>
          <p className="aib-hint">
            Skipping still drafts from the messages, and the assistant is told what it did not read.
          </p>
        </div>
      )}

      {phase === 'error' && (
        <div className="aib-error">
          {error}
          <div className="aib-actions">
            <button
              type="button"
              className="cbtn cbtn-sm"
              onClick={() => void generate(clarificationsRef.current, attachmentMode)}
            >
              Try again
            </button>
          </div>
        </div>
      )}

      {/* Above the questions and above the form alike: the answer changes
          whether to fill either in at all, so it has to be readable before the
          dispatcher starts typing rather than found underneath their work. */}
      {(phase === 'editing' || phase === 'needs_info') && (
        <DuplicateWarning result={duplicates} />
      )}

      {phase === 'needs_info' && (
        <div className="aib-qa">
          <p className="aib-qa-lead">The AI needs a little more before it can draft this reservation:</p>
          <ol>
            {questions.map((question, index) => (
              <li key={question.text}>
                <span>{question.text}</span>
                <QuestionAnswer
                  question={question}
                  value={answers[index] ?? ''}
                  customOpen={customOpen[index] ?? false}
                  onValue={(value) =>
                    setAnswers((prev) => prev.map((a, i) => (i === index ? value : a)))
                  }
                  onCustomOpen={(open) =>
                    setCustomOpen((prev) => prev.map((c, i) => (i === index ? open : c)))
                  }
                />
              </li>
            ))}
          </ol>
          <div className="aib-actions">
            <button
              type="button"
              className="cbtn cbtn-sm cbtn-primary"
              disabled={answers.every((a) => !a.trim())}
              onClick={answerAndRegenerate}
            >
              Answer & regenerate
            </button>
            {partial && (
              <button
                type="button"
                className="cbtn cbtn-sm"
                onClick={() => seedForm(partial, booker, prefill)}
              >
                Use what it found
              </button>
            )}
          </div>
        </div>
      )}

      {phase === 'editing' && form && (
        <EditingBody
          form={form}
          setForm={setForm}
          patchLeg={patchLeg}
          dispatch={dispatch}
          vehicles={vehicles}
          vehiclesError={vehiclesError}
          prefill={prefill}
          cards={cards}
          onPayment={setPayment}
          onBookerType={setBookerType}
          onSearchBooker={bookerLinks ? () => setPickerOpen((open) => !open) : undefined}
          bookerExtras={
            <>
              {form.bookerType === 'guest' && !pickerOpen && (
                <MaybeBookers
                  suggestions={bookerSuggestions}
                  busy={remembering}
                  onPick={(pick) => void pickBooker(pick)}
                />
              )}
              {pickerOpen && bookerLinks && (
                <BookerPicker
                  contactId={contact.id}
                  suggest={bookerLinks.suggest}
                  initial={bookerSuggestions}
                  title="Who is booking this?"
                  busy={remembering}
                  onPick={(pick) => void pickBooker(pick)}
                  onClose={() => setPickerOpen(false)}
                  footer={
                    rememberFor ? (
                      <div className="bkp-remember-row">
                        <label className="bkp-remember">
                          <input
                            type="checkbox"
                            checked={remember}
                            onChange={(e) => setRemember(e.target.checked)}
                          />
                          Remember for {rememberFor}
                        </label>
                        <InfoTip
                          label="What remembering does"
                          text="The next reservation drafted from this address opens on the account you pick, and a customer picked here becomes the contact’s dispatch link. Untick it for a one-off, like an assistant reservation for somebody else."
                        />
                      </div>
                    ) : null
                  }
                />
              )}
              {rememberNote && (
                <span className={`aib-hint aib-note${rememberNote.ok ? '' : ' aib-hint-err'}`}>
                  {rememberNote.text}
                  {rememberNote.tip && <InfoTip text={rememberNote.tip} label="About this" />}
                </span>
              )}
            </>
          }
          submitting={submitting}
          submitError={submitError}
          menu={menu}
          setMenu={setMenu}
          onCancel={onClose}
          onSubmit={submit}
        />
      )}
    </div>
  );
}

/**
 * "This looks like something we already have."
 *
 * The one thing this component may never do is imply a clean bill of health it
 * was not given: `checked: false` is an ungranted column or an unreachable
 * dispatch, and it renders as a grey note saying the check could not run —
 * never as silence, which reads identically to "no duplicates found" and is
 * the exact mistake the whole feature exists to prevent.
 *
 * It does not block the save. A resent reservation is usually a duplicate and
 * occasionally a genuine second car for the same party, and only the person
 * reading the thread can tell the two apart. So this is amber and informative
 * rather than red and refusing — the dispatcher is being told, not stopped.
 */
function DuplicateWarning({ result }: { result: BookingDuplicateResult | null }) {
  if (!result) return null;

  if (!result.checked) {
    return (
      <div className="aib-dupes is-unchecked">
        <p className="aib-dupe-lead">
          <TriangleAlert size={13} />
          <span>
            Couldn&rsquo;t check whether this trip is already booked
            {result.reason ? ` — ${result.reason}` : ''}. Search dispatch before you save.
          </span>
        </p>
      </div>
    );
  }
  if (!result.matches.length) return null;

  const { ourReference, bookerReference } = result.references;
  return (
    <div className="aib-dupes">
      <p className="aib-dupe-lead">
        <TriangleAlert size={13} />
        <span>
          {result.matches.length === 1
            ? 'A reservation already on file quotes'
            : 'Reservations already on file quote'}{' '}
          {/* Named rather than described: "Ref #40112" and "Client Ref #850675"
              are different situations, and a dispatcher who cannot tell which
              matched cannot tell a resend from a reply to our confirmation. */}
          {[
            ourReference ? <b key="ours">{ourRef('job', ourReference)}</b> : null,
            bookerReference ? <b key="theirs">{clientRef(bookerReference)}</b> : null,
          ]
            .filter(Boolean)
            .reduce<React.ReactNode[]>(
              (parts, node, index) => (index ? [...parts, ' and ', node] : [node]),
              [],
            )}
          . Check before booking this again.
        </span>
      </p>
      <ul className="aib-dupe-list">
        {result.matches.map((match) => {
          const reference = match.job?.reference ?? match.booking?.reference ?? match.dispatchId;
          const trip =
            match.job
              ? [match.job.pickup, match.job.dropoff].filter(Boolean).join(' → ')
              : (match.booking?.summary ?? '');
          return (
            <li className="aib-dupe" key={`${match.kind}:${match.dispatchId}`}>
              <span className="aib-dupe-why">{match.matchedOn.uid ? 'same Ref' : 'same Client Ref'}</span>
              <span className="aib-dupe-trip" title={trip}>
                <JobLink kind={match.kind} id={match.dispatchId} className="aib-dupe-ref">
                  {ourRef(match.kind, reference)}
                </JobLink>
                {match.job?.status.label ? ` · ${match.job.status.label}` : ''}
                {trip ? ` · ${trip}` : ''}
              </span>
              {/* Inside the dashboard it opens there; elsewhere it links out;
                  with nowhere to go it is not drawn at all. */}
              <JobLink kind={match.kind} id={match.dispatchId} className="aib-dupe-open" icon plain="none">
                Open
              </JobLink>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * The answer control for one clarifying question, picked by its kind: option
 * chips for a closed set (with an "Other…" escape into free text — the AI's
 * candidates are read from the thread and can all be wrong), native pickers
 * for date/time answers, and a plain input for everything else. Every kind
 * still lands in the same string the model gets back.
 */
function QuestionAnswer({
  question,
  value,
  customOpen,
  onValue,
  onCustomOpen,
}: {
  question: UiQuestion;
  value: string;
  customOpen: boolean;
  onValue: (value: string) => void;
  onCustomOpen: (open: boolean) => void;
}) {
  if (question.kind === 'choice') {
    return (
      <>
        <div className="aib-opts">
          {question.options.map((option) => (
            <button
              key={option}
              type="button"
              className={`aib-opt${!customOpen && value === option ? ' on' : ''}`}
              onClick={() => {
                onCustomOpen(false);
                // Clicking the selected chip again deselects it.
                onValue(!customOpen && value === option ? '' : option);
              }}
            >
              {option}
            </button>
          ))}
          <button
            type="button"
            className={`aib-opt${customOpen ? ' on' : ''}`}
            onClick={() => {
              onValue('');
              onCustomOpen(!customOpen);
            }}
          >
            Other…
          </button>
        </div>
        {customOpen && (
          <input
            className="aib-input"
            type="text"
            autoFocus
            placeholder="Type the answer…"
            value={value}
            onChange={(e) => onValue(e.target.value)}
          />
        )}
      </>
    );
  }
  const type =
    question.kind === 'date'
      ? 'date'
      : question.kind === 'time'
        ? 'time'
        : question.kind === 'datetime'
          ? 'datetime-local'
          : question.kind === 'number'
            ? 'number'
            : 'text';
  const hasPicker = type === 'date' || type === 'time' || type === 'datetime-local';
  return (
    <input
      className="aib-input"
      type={type}
      {...(type === 'number' ? { min: 0 } : {})}
      {...(hasPicker ? { onClick: openNativePicker } : {})}
      value={value}
      onChange={(e) => onValue(e.target.value)}
    />
  );
}

/**
 * Who is placing the job, and WHY the form thinks so.
 *
 * The chip always says where the booker came from rather than asserting a
 * company out of nowhere: the address that MATCHED (`res@discoverbespoke.com`),
 * an agent's earlier answer for this address, or this agent's pick just now.
 * Only an unverified address match is a guess, and only that is tinted as one.
 *
 * The draft's own alternatives stay one tap away, and "Find in dispatch…"
 * opens the search for everything else — a regular writing from a new mailbox,
 * or a desk the draft matched wrongly.
 */
function BookerChip({
  form,
  onBookerType,
  onSearch,
}: {
  form: BookingFormState;
  onBookerType: (type: BookerType) => void;
  onSearch?: () => void;
}) {
  const affiliate = form.bookerAffiliate;
  const customer = form.bookerCustomer;
  const verified = bookerVerified(form);
  const why =
    form.bookerSource === 'picked'
      ? ' · picked just now'
      : form.bookerSource === 'remembered'
        ? ' · remembered for this address'
        : null;
  const label =
    form.bookerType === 'affiliate' && affiliate ? (
      <>
        Booker: <strong>{affiliate.companyName || `affiliate #${affiliate.id}`}</strong> · affiliate
        {why ?? (affiliate.matchedEmail ? ` · matched ${affiliate.matchedEmail}` : '')}
      </>
    ) : form.bookerType === 'customer' && customer ? (
      <>
        Booker: <strong>{customer.name || `customer #${customer.id}`}</strong> · dispatch customer #
        {customer.id}
        {verified ? ' · verified' : (why ?? ' · unverified guess')}
      </>
    ) : (
      <>Booker: booking as guest</>
    );
  // An unverified address match is a GUESS; an affiliate hit is an exact
  // address on a row somebody set up, and a pick is a person's decision.
  const unverified = form.bookerType === 'customer' && !verified && form.bookerSource === 'match';
  return (
    <span className={`aib-chip${unverified ? ' unverified' : ''}`}>
      {label}
      {affiliate && form.bookerType !== 'affiliate' ? (
        <button type="button" onClick={() => onBookerType('affiliate')}>
          Book as {affiliate.companyName || 'the affiliate'}
        </button>
      ) : null}
      {customer && form.bookerType !== 'customer' ? (
        <button type="button" onClick={() => onBookerType('customer')}>
          Book as {customer.name || `customer #${customer.id}`}
        </button>
      ) : null}
      {form.bookerType !== 'guest' ? (
        <button type="button" onClick={() => onBookerType('guest')}>
          Book as guest instead
        </button>
      ) : null}
      {onSearch ? (
        <button type="button" onClick={onSearch}>
          {form.bookerType === 'guest' ? 'Find in dispatch…' : 'Someone else…'}
        </button>
      ) : null}
    </span>
  );
}

/**
 * "Possibly: John Smith · Acme Corp" under a Guest chip — the draft found
 * nobody exact, and these are the likeliest accounts. Offered, never applied:
 * the same domain is where somebody works, not who they are.
 */
function MaybeBookers({
  suggestions,
  busy,
  onPick,
}: {
  suggestions: BookerSuggestions | null;
  busy: boolean;
  onPick: (pick: BookerPick) => void;
}) {
  const candidates = topCandidates(suggestions);
  if (!candidates.length) return null;
  return (
    <div className="aib-maybe">
      <span>Possibly:</span>
      {candidates.map((candidate) => (
        <button
          key={candidate.key}
          type="button"
          title={candidate.hint ? `Suggested: ${candidate.hint}` : undefined}
          disabled={busy}
          onClick={() => onPick(candidate.pick)}
        >
          {candidate.label}
        </button>
      ))}
    </div>
  );
}

function EditingBody({
  form,
  setForm,
  patchLeg,
  dispatch,
  vehicles,
  vehiclesError,
  prefill,
  cards,
  onPayment,
  onBookerType,
  onSearchBooker,
  bookerExtras,
  submitting,
  submitError,
  menu,
  setMenu,
  onCancel,
  onSubmit,
}: {
  form: BookingFormState;
  setForm: React.Dispatch<React.SetStateAction<BookingFormState | null>>;
  patchLeg: (key: 'pickup' | 'dropoff' | number, leg: LegState) => void;
  dispatch: DispatchClient;
  vehicles: VehicleTypeItem[] | null;
  vehiclesError: string | null;
  /** The tenant's switches — here for the vehicle pick, which fills the counts. */
  prefill: BookingPrefill;
  cards: CardLookup;
  onPayment: (mode: PaymentMode, methodId: string) => void;
  onBookerType: (type: BookerType) => void;
  onSearchBooker?: () => void;
  /** The suggestions, the picker and the "saved for next time" line, under the chip. */
  bookerExtras?: React.ReactNode;
  submitting: SubmitKind | null;
  submitError: string | null;
  menu: 'save' | null;
  setMenu: React.Dispatch<React.SetStateAction<'save' | null>>;
  onCancel: () => void;
  onSubmit: (kind: 'draft' | 'job', isQuote: boolean) => Promise<void>;
}) {
  const { foreignZoneAbbr, fmtWallTime, zone: dispatchZone } = useTimeFormat();
  const menuRef = useDismissOnOutside<HTMLDivElement>(menu !== null, () => setMenu(null));
  const set = <K extends keyof BookingFormState>(key: K, value: BookingFormState[K]) =>
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));

  /**
   * The pickup zone only earns a badge when it is NOT the dispatch clock — an
   * "EDT" tag on every New York pickup is noise, but a Los Angeles pickup shown
   * as a bare "15:00" next to New York times is a missed flight.
   */
  const pickupZone = form.timeZoneId;
  const pickupZoneAbbr = foreignZoneAbbr(pickupZone);
  const zoneSuffix = pickupZoneAbbr ? ` ${pickupZoneAbbr}` : '';

  // Split, because a quote is exempt from the payment choice — one shared
  // gate would either block a legitimate quote or offer a job save dispatch
  // is going to refuse.
  const pickupAirport = pickupAirportCode(form.pickup);
  const jobValidation = validateForSave(form, { isQuote: false });
  const quoteValidation = validateForSave(form, { isQuote: true });
  const busy = submitting !== null;
  const applyOn = form.serviceType === 'hourly' ? 'H' : 'O';
  const hourly = form.serviceType === 'hourly';

  const [openSections, setOpenSections] = useState<Record<BookingSectionId, boolean> | null>(null);
  /** Set on a blocked save — turns on the callout and the field highlights. */
  const [attempted, setAttempted] = useState<'job' | 'quote' | null>(null);
  const sectionRefs = useRef<Partial<Record<BookingSectionId, HTMLElement | null>>>({});

  const legsBusy =
    form.pickup.resolving || form.dropoff.resolving || form.stops.some((s) => s.resolving);
  const settled = vehicles !== null && !legsBusy;

  // What starts open is decided ONCE, at the render where the seed's silent
  // lookups have landed — judged before them, every card reads "missing" and
  // all of them would open. Render-time adjustment rather than an effect, so
  // the decision commits in the same paint it was judged in. From then on the
  // cards only move for the agent (or a blocked save below).
  if (settled && openSections === null) {
    const opened = { ...ALL_CLOSED };
    for (const item of jobValidation.missing) opened[item.section] = true;
    setOpenSections(opened);
  }

  const isOpen = (id: BookingSectionId) => openSections?.[id] ?? false;
  const toggleSection = (id: BookingSectionId) =>
    setOpenSections((prev) => ({ ...(prev ?? ALL_CLOSED), [id]: !(prev?.[id] ?? false) }));
  const openSection = (id: BookingSectionId) => {
    setOpenSections((prev) => ({ ...(prev ?? ALL_CLOSED), [id]: true }));
    sectionRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  // The strict (job) gate drives the badges; `attempted` only picks which list
  // the callout and the field highlights follow after a blocked save.
  const activeValidation = attempted === 'quote' ? quoteValidation : jobValidation;
  const showNeed = attempted !== null && !activeValidation.ok;
  const miss = (key: MissingKey) =>
    attempted !== null && activeValidation.missing.some((m) => m.key === key);
  const inputCls = (key: MissingKey) => `aib-input${miss(key) ? ' aib-miss' : ''}`;
  const sectionMissing = (id: BookingSectionId) => missingInSection(jobValidation, id).length;

  /**
   * Save is never disabled on validation — a dead button cannot explain
   * itself. A blocked click opens every card with a gap and lists the gaps
   * as clickable chips instead of submitting anything.
   */
  const requestSave = (isQuote: boolean) => {
    setMenu(null);
    const validation = isQuote ? quoteValidation : jobValidation;
    if (!validation.ok) {
      setAttempted(isQuote ? 'quote' : 'job');
      setOpenSections((prev) => {
        const next = { ...(prev ?? ALL_CLOSED) };
        for (const item of validation.missing) next[item.section] = true;
        return next;
      });
      return;
    }
    void onSubmit('job', isQuote);
  };

  // Collapsed-row summaries — what the AI filled in, reviewable at a glance.
  const routeText = [
    form.pickup.text.trim() || '—',
    hourly && form.dropoffSameAsPickup ? 'same as pickup' : form.dropoff.text.trim() || '—',
  ].join(' → ');
  const tripSummary = [
    hourly ? `Hourly${Number(form.durationHours) > 0 ? ` ${form.durationHours}h` : ''}` : 'One-way',
    routeText,
    form.stops.length ? `${form.stops.length} stop${form.stops.length > 1 ? 's' : ''}` : '',
    form.pickupDatetime ? `${fmtWallTime(form.pickupDatetime)}${zoneSuffix}` : 'no time yet',
  ]
    .filter(Boolean)
    .join(' · ');
  const passengerSummary = [
    `${form.passengerFirstName} ${form.passengerLastName}`.trim() || 'no name',
    form.passengerPhone.trim() || 'no phone',
    form.passengerEmail.trim() || 'no email',
    form.passengerCount ? `${form.passengerCount} pax` : '',
    form.luggageCount ? `${form.luggageCount} lug` : '',
  ]
    .filter(Boolean)
    .join(' · ');
  const chosenVehicle = (vehicles ?? []).find((v) => v.id === form.vehicleTypeId)?.name;
  const vehicleSummary = [
    chosenVehicle ??
      (form.vehiclePreference ? `AI suggested “${form.vehiclePreference}”` : 'no vehicle yet'),
    [form.flightNumber, form.airline].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(' · ');
  const noteKinds = [form.customerNote && 'customer', form.chauffeurNote && 'chauffeur'].filter(
    Boolean,
  );
  const notesSummary = noteKinds.length
    ? `${noteKinds.join(' + ')} note${noteKinds.length > 1 ? 's' : ''}`
    : 'none';
  const bookerSummary =
    form.bookerType === 'affiliate'
      ? form.bookerAffiliate?.companyName || `affiliate #${form.bookerAffiliate?.id}`
      : form.bookerType === 'customer'
        ? form.bookerCustomer?.name || `customer #${form.bookerCustomer?.id}`
        : form.bookerName.trim() || 'guest';
  const selectedCard =
    cards.status === 'ready' ? cards.cards.find((c) => c.id === form.paymentMethodId) : undefined;
  const paymentSummary =
    form.paymentMode === 'billLater'
      ? 'Bill later'
      : form.paymentMode === 'paymentLink'
        ? 'Send a payment link'
        : selectedCard
          ? `${
              selectedCard.brand
                ? selectedCard.brand.charAt(0).toUpperCase() + selectedCard.brand.slice(1)
                : 'Card'
            }${selectedCard.last4 ? ` ···· ${selectedCard.last4}` : ''}`
          : 'Card';

  return (
    <>
      {vehiclesError && (
        <div className="aib-warn">
          Dispatch is unreachable from this browser — drafts and saves will not go through.{' '}
          {vehiclesError}
        </div>
      )}
      {form.returnTripRequested && (
        <div className="aib-warn">
          The customer also asked about a return trip — book it as a second job.
        </div>
      )}

      <div className="aib-chiprow">
        <BookerChip form={form} onBookerType={onBookerType} onSearch={onSearchBooker} />
        {bookerExtras}
      </div>

      <div className="aib-secs">
        <FormSection
          title="Trip"
          summary={tripSummary}
          missingCount={sectionMissing('trip')}
          busy={legsBusy}
          open={isOpen('trip')}
          onToggle={() => toggleSection('trip')}
          bodyRef={(el) => {
            sectionRefs.current.trip = el;
          }}
        >
          <div className="aib-grid">
            <label className="aib-field">
              <span className="aib-label">Service</span>
              <select
                className="aib-input"
                value={form.serviceType}
                onChange={(e) =>
                  set('serviceType', e.target.value === 'hourly' ? 'hourly' : 'oneway')
                }
              >
                <option value="oneway">One-way</option>
                <option value="hourly">Hourly</option>
              </select>
            </label>
            <label className="aib-field">
              <span className="aib-label">
                Pickup time
                {pickupZoneAbbr ? (
                  <span className="aib-zone" title={pickupZone ?? undefined}>
                    {pickupZoneAbbr}
                  </span>
                ) : null}
              </span>
              <input
                className={inputCls('pickupTime')}
                type="datetime-local"
                value={form.pickupDatetime}
                onClick={openNativePicker}
                onChange={(e) => set('pickupDatetime', e.target.value)}
              />
              {pickupZoneAbbr ? (
                <span className="aib-hint">
                  Local to the pickup — {dispatchZone ? 'dispatch runs on' : 'your clock is'}{' '}
                  {dispatchZone ?? 'a different zone'}.
                </span>
              ) : null}
            </label>
            {hourly && (
              <label className="aib-field">
                <span className="aib-label">Duration (hours)</span>
                <input
                  className={inputCls('duration')}
                  type="number"
                  min={1}
                  value={form.durationHours}
                  onChange={(e) => set('durationHours', e.target.value)}
                />
              </label>
            )}

            <AddressField
              label="Pickup (PU)"
              leg={form.pickup}
              applyOn={applyOn}
              dispatch={dispatch}
              missing={miss('pickupAddress')}
              onChange={(leg) => patchLeg('pickup', leg)}
            />
            {hourly && (
              <label className="aib-field aib-check">
                <input
                  type="checkbox"
                  checked={form.dropoffSameAsPickup}
                  onChange={(e) => set('dropoffSameAsPickup', e.target.checked)}
                />
                <span>Dropoff same as pickup</span>
              </label>
            )}
            {!(hourly && form.dropoffSameAsPickup) && (
              <AddressField
                label="Dropoff (DO)"
                leg={form.dropoff}
                applyOn={applyOn}
                dispatch={dispatch}
                missing={miss('dropoffAddress')}
                onChange={(leg) => patchLeg('dropoff', leg)}
              />
            )}
            {form.stops.map((stop, index) => (
              <AddressField
                // Stops have no stable id; index identity is fine for a short static list.
                key={`stop-${index}`}
                label={`Stop ${index + 1}`}
                leg={stop}
                dispatch={dispatch}
                onChange={(leg) => patchLeg(index, leg)}
              />
            ))}
          </div>
        </FormSection>

        <FormSection
          title="Passenger"
          summary={passengerSummary}
          missingCount={sectionMissing('passenger')}
          open={isOpen('passenger')}
          onToggle={() => toggleSection('passenger')}
          bodyRef={(el) => {
            sectionRefs.current.passenger = el;
          }}
        >
          <div className="aib-grid">
            <label className="aib-field">
              <span className="aib-label">First name</span>
              <input
                className={inputCls('firstName')}
                type="text"
                value={form.passengerFirstName}
                onChange={(e) => set('passengerFirstName', e.target.value)}
              />
            </label>
            <label className="aib-field">
              <span className="aib-label">Last name</span>
              <input
                className={inputCls('lastName')}
                type="text"
                value={form.passengerLastName}
                onChange={(e) => set('passengerLastName', e.target.value)}
              />
            </label>
            <label className="aib-field">
              <span className="aib-label">Phone</span>
              <input
                className={inputCls('phone')}
                type="tel"
                value={form.passengerPhone}
                onChange={(e) => set('passengerPhone', e.target.value)}
              />
            </label>
            <label className="aib-field">
              <span className="aib-label">Email</span>
              <input
                className={inputCls('email')}
                type="email"
                value={form.passengerEmail}
                onChange={(e) => set('passengerEmail', e.target.value)}
              />
              {miss('email') && form.passengerEmail.trim() && (
                <span className="aib-hint">Doesn’t look like an email address.</span>
              )}
            </label>
            <label className="aib-field">
              <span className="aib-label">Passengers (PAX)</span>
              <input
                className={inputCls('pax')}
                type="number"
                min={1}
                value={form.passengerCount}
                onChange={(e) => set('passengerCount', e.target.value)}
              />
            </label>
            <label className="aib-field">
              <span className="aib-label">Luggage (LUG)</span>
              <input
                className={inputCls('luggage')}
                type="number"
                min={0}
                value={form.luggageCount}
                onChange={(e) => set('luggageCount', e.target.value)}
              />
              {miss('luggage') && (
                // The one required count with a legitimate zero — an agent who
                // reads "required" as "type something" needs to be told 0 is it.
                <span className="aib-hint">Enter 0 when the party travels with no bags.</span>
              )}
            </label>
          </div>
        </FormSection>

        {/*
          The BOOKER, kept out of the Passenger card on purpose: on a farm-in
          they are different people, and one card holding both is what let the
          affiliate's booking agent be saved as the person standing at the curb.
          Nothing here is ever required — dispatch accepts a guest booking with
          all three blank — so the card carries no missing count.
        */}
        <FormSection
          title="Booker"
          summary={bookerSummary}
          missingCount={0}
          open={isOpen('booker')}
          onToggle={() => toggleSection('booker')}
          bodyRef={(el) => {
            sectionRefs.current.booker = el;
          }}
        >
          <div className="aib-grid">
            {form.bookerType === 'guest' ? (
              <>
                <label className="aib-field">
                  <span className="aib-label">Booked by</span>
                  <input
                    className="aib-input"
                    value={form.bookerName}
                    onChange={(e) => set('bookerName', e.target.value)}
                  />
                </label>
                <label className="aib-field">
                  <span className="aib-label">Booker email</span>
                  <input
                    className="aib-input"
                    value={form.bookerEmail}
                    onChange={(e) => set('bookerEmail', e.target.value)}
                  />
                </label>
                <label className="aib-field">
                  <span className="aib-label">Booker phone</span>
                  <input
                    className="aib-input"
                    value={form.bookerPhone}
                    onChange={(e) => set('bookerPhone', e.target.value)}
                  />
                </label>
              </>
            ) : (
              <div className="aib-field aib-span2">
                {/* The billing half stays on screen — it is the one thing worth
                    checking before saving; the rest is behind the "?". */}
                <span className="aib-hint aib-note">
                  {form.bookerType === 'affiliate'
                    ? 'Billed to the affiliate’s account'
                    : 'Booked on the customer’s account'}
                  <InfoTip
                    label="About the booker"
                    text={
                      form.bookerType === 'affiliate'
                        ? 'The affiliate answers for the booker fields — dispatch bills this job to their account. Use the chip above to book as a guest instead.'
                        : 'The dispatch customer answers for the booker fields. Use the chip above to book as a guest instead.'
                    }
                  />
                </span>
              </div>
            )}
            {form.bookerType === 'affiliate' && (
              <label className="aib-field">
                {/*
                  requesterResNo — the affiliate's OWN number for this trip, which
                  dispatch quotes back to them. Affiliate bookings only, because
                  that is the only shape the job payload carries it in.
                */}
                <span className="aib-label">Client Ref #</span>
                <input
                  className="aib-input"
                  value={form.bookerReference}
                  onChange={(e) => set('bookerReference', e.target.value)}
                />
              </label>
            )}
          </div>
        </FormSection>

        <FormSection
          title="Vehicle & flight"
          summary={vehicleSummary}
          missingCount={sectionMissing('vehicle')}
          busy={vehicles === null}
          open={isOpen('vehicle')}
          onToggle={() => toggleSection('vehicle')}
          bodyRef={(el) => {
            sectionRefs.current.vehicle = el;
          }}
        >
          <div className="aib-grid">
            <label className="aib-field">
              <span className="aib-label">Vehicle type</span>
              <select
                className={inputCls('vehicle')}
                value={form.vehicleTypeId}
                onChange={(e) => {
                  const id = Number(e.target.value);
                  setForm((prev) =>
                    prev
                      ? {
                          ...prev,
                          vehicleTypeId: id,
                          ...countsFromVehicle(
                            prev,
                            (vehicles ?? []).find((v) => v.id === id),
                            prefill,
                          ),
                        }
                      : prev,
                  );
                }}
              >
                <option value={0}>{vehicles === null ? 'Loading…' : 'Choose a vehicle'}</option>
                {(vehicles ?? [])
                  .filter((v) => v.isActive !== false)
                  .map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name}
                    </option>
                  ))}
              </select>
              {form.vehiclePreference && !form.vehicleTypeId && (
                <span className="aib-hint">AI suggested: “{form.vehiclePreference}”</span>
              )}
            </label>
            <label className="aib-field">
              <span className="aib-label">Flight #</span>
              <input
                className="aib-input"
                type="text"
                value={form.flightNumber}
                onChange={(e) => set('flightNumber', e.target.value)}
              />
              {/* Visible because it is silent otherwise: this code is what narrows
                  dispatch's flight lookup to the leg landing at this pickup. */}
              {pickupAirport && <span className="aib-hint">Arrival airport {pickupAirport}</span>}
            </label>
            <label className="aib-field">
              <span className="aib-label">Airline</span>
              <input
                className="aib-input"
                type="text"
                value={form.airline}
                onChange={(e) => set('airline', e.target.value)}
              />
            </label>
          </div>
        </FormSection>

        <FormSection
          title="Notes"
          summary={notesSummary}
          missingCount={0}
          open={isOpen('notes')}
          onToggle={() => toggleSection('notes')}
          bodyRef={(el) => {
            sectionRefs.current.notes = el;
          }}
        >
          <div className="aib-grid">
            <label className="aib-field aib-span2">
              <span className="aib-label">Customer note</span>
              <textarea
                className="aib-textarea"
                rows={2}
                value={form.customerNote}
                onChange={(e) => set('customerNote', e.target.value)}
              />
            </label>
            <label className="aib-field aib-span2">
              <span className="aib-label">Chauffeur note</span>
              <textarea
                className="aib-textarea"
                rows={2}
                value={form.chauffeurNote}
                onChange={(e) => set('chauffeurNote', e.target.value)}
              />
            </label>
          </div>
        </FormSection>

        <FormSection
          title="Payment"
          summary={paymentSummary}
          missingCount={sectionMissing('payment')}
          busy={cards.status === 'loading'}
          open={isOpen('payment')}
          onToggle={() => toggleSection('payment')}
          bodyRef={(el) => {
            sectionRefs.current.payment = el;
          }}
        >
          <PaymentSection
            mode={form.paymentMode}
            methodId={form.paymentMethodId}
            lookup={cards}
            disabled={busy}
            onChange={onPayment}
          />
        </FormSection>
      </div>

      {showNeed && (
        <div className="aib-need">
          <TriangleAlert size={13} />
          <div className="aib-need-body">
            <span>To save as a {attempted === 'quote' ? 'quote' : 'job'}, still add:</span>
            <div className="aib-need-chips">
              {activeValidation.missing.map((item) => (
                <button key={item.key} type="button" onClick={() => openSection(item.section)}>
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {submitError && <div className="aib-error">{submitError}</div>}

      <div className="aib-foot">
        <button type="button" className="cbtn cbtn-sm" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <label className="aib-toggle" title="Emails the customer their confirmation from dispatch">
          <input
            type="checkbox"
            checked={form.sendNotifications}
            onChange={(e) => set('sendNotifications', e.target.checked)}
          />
          <span>Send notifications</span>
        </label>
        <span className="aib-foot-space" />

        {/* One button, not a job/quote pair: dispatch parks ONE draft and the
            job-or-quote choice is made in Add Job when it is booked. Never
            validated — parking an incomplete booking is the whole point. */}
        <button
          type="button"
          className="cbtn cbtn-sm"
          title="Parks this in dispatch's Drafts list — nothing is validated, and job or quote is chosen when it's booked"
          disabled={busy}
          onClick={() => void onSubmit('draft', false)}
        >
          {submitting === 'draft' && <LoaderCircle size={12} className="spin-anim" />}
          Save as draft
        </button>

        <div className="cbtn-split">
          {/* Enabled even while incomplete: a blocked click explains itself
              (the callout above) instead of submitting — see requestSave. */}
          <button
            type="button"
            className={`cbtn cbtn-sm cbtn-brand${jobValidation.ok ? '' : ' cbtn-unready'}`}
            title={
              jobValidation.ok
                ? undefined
                : `Still needed: ${jobValidation.missing.map((m) => m.label).join(', ')}`
            }
            disabled={busy}
            onClick={() => requestSave(false)}
          >
            {submitting === 'saveJob' && <LoaderCircle size={12} className="spin-anim" />}
            Save as job
          </button>
          <button
            type="button"
            className="cbtn cbtn-sm cbtn-brand cbtn-split-caret"
            disabled={busy}
            onClick={() => setMenu(menu === 'save' ? null : 'save')}
          >
            <ChevronUp size={12} />
          </button>
          {menu === 'save' && (
            <div className="thr-menu aib-menu-up" ref={menuRef}>
              <button
                type="button"
                title={
                  quoteValidation.ok
                    ? undefined
                    : `Still needed: ${quoteValidation.missing.map((m) => m.label).join(', ')}`
                }
                onClick={() => requestSave(true)}
              >
                {submitting === 'saveQuote' && <LoaderCircle size={12} className="spin-anim" />}
                Save as quote
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
